'use client';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@askable/ui/core/alert-dialog';
import { DropdownMenuItem } from '@askable/ui/core/dropdown-menu';
import { MessageCircleWarning } from 'lucide-react';
import { createContext, useContext } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { useIntercomMessenger } from '@/hooks/useIntercomMessenger/useIntercomMessenger';

const IntercomContext = createContext<{ showIntercomMessages: () => void }>({
  showIntercomMessages: () => {},
});

const IntercomFallbackDialog = ({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) => {
  const { t } = useTranslation();

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('global.liveChatUnavailable')}</AlertDialogTitle>
        </AlertDialogHeader>
        <AlertDialogDescription>
          <Trans
            i18nKey="global.liveChatUnavailableDescription"
            // `Trans` fills the anchor from the translation string at runtime.
            // eslint-disable-next-line jsx-a11y/anchor-has-content -- Trans fills the anchor text from the translation string
            components={{ 1: <a href="mailto:support@askable.com" className="underline" /> }}
          />
        </AlertDialogDescription>
        <AlertDialogFooter>
          <AlertDialogAction onClick={() => onOpenChange(false)}>{t('global.ok')}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

/**
 * Provides the Intercom messenger hook and renders the fallback dialog.
 * Must wrap the DropdownMenu that contains IntercomMenuItem so the dialog
 * survives dropdown unmount.
 */
export const IntercomMenuItemProvider = ({ children }: { children: React.ReactNode }) => {
  const { showIntercomMessages, isFallbackOpen, setIsFallbackOpen } = useIntercomMessenger();

  return (
    <IntercomContext.Provider value={{ showIntercomMessages }}>
      {children}
      <IntercomFallbackDialog open={isFallbackOpen} onOpenChange={setIsFallbackOpen} />
    </IntercomContext.Provider>
  );
};

export const IntercomMenuItem = ({ onClick }: { onClick?: () => void }) => {
  const { t } = useTranslation();
  const { showIntercomMessages } = useContext(IntercomContext);

  return (
    <DropdownMenuItem
      className="cursor-pointer"
      onClick={() => {
        showIntercomMessages();
        onClick?.();
      }}
    >
      <MessageCircleWarning className="h-4 w-4" />
      {t('global.contactSupport')}
    </DropdownMenuItem>
  );
};
